import tenantTheme from '@theme';
import tenantConstants from '@constants';
import { NetworkService } from '../../../../services/networkService';
import { getDateLabels, getVariousDates } from '../../../../utility/date';
import { getMultiValuesQueryString } from '../../../../utility/urlQuery';
import moment from 'moment';
import { getPhoneStats } from '../ziwo-tracking/getPhoneStats';
import { t } from 'i18next';

const tabItems = [
  {
    key: 'calls',
    title: 'Calls',
  },
  {
    key: 'whatsapp',
    title: 'WhatsApp',
  },

  {
    key: 'emails',
    title: 'Emails',
  },
];

const productTypes = [
  {
    key: 'all',
    title: 'All Listings',
    color: tenantTheme['primary-color'],
  },
  {
    key: 'premium',
    title: 'Basic',
    color: tenantTheme['color-basic'],
    icon: 'IconBasic',
    borderColor: tenantTheme['color-basic'],

    iconProps: {
      color: tenantTheme['color-basic'],
    },
  },
  {
    key: 'hot',
    title: 'Hot',
    icon: 'IconSuperHot',
    color: tenantTheme['color-hot'],
    iconProps: {
      color: tenantTheme['color-hot'],
    },
  },
  {
    key: 'superhot',
    title: 'Signature',
    icon: 'BsFillLightningChargeFill',
    color: tenantTheme['color-signature'],
    iconProps: {
      color: tenantTheme['color-signature'],
    },
  },
];

const graphDataMapper = (leadsStats, user, phoneLeadsStats, labels) => {
  const metricMapping = {
    calls: {
      calls_clicked: {
        responseKey: 'sum_phone_view_count',
        borderColor: '#006169',
        label: 'Calls Clicked',
      },
    },
    whatsapp: {
      whatsapp_clicked: {
        responseKey: 'sum_whatsapp_view_count',
        borderColor: '#006169',
        label: 'WhatsApp Clicked',
      },
      ...(user?.is_lms_enabled &&
        user?.is_whatsapp_tracking_enabled &&
        tenantConstants.IS_LMS_ENABLED && {
          whatsapp_sent: {
            responseKey: 'sum_whatsapp_lead_count',
            borderColor: '#28B16D',
            label: 'WhatsApp Sent',
          },
          chats_initiated: {
            responseKey: 'chats_initiated', //TODO : Update Key
            borderColor: '#F7ADA0',
            label: 'Chats Initiated',
          },
        }),
    },
    emails: {
      emails_received: {
        responseKey: 'sum_email_lead_count',
        label: 'Emails Received',
        borderColor: '#006169',
      },
    },
  };

  const graphData = {};

  Object.keys(metricMapping).forEach((category) => {
    graphData[category] = {};
    Object.keys(metricMapping[category]).forEach((key) => {
      graphData[category][key] = {
        label: metricMapping?.[category]?.[key]?.label,
        borderColor: metricMapping?.[category]?.[key]?.borderColor,
        ...productTypes.reduce((acc, { key: productKey }) => {
          acc[productKey] = [];
          return acc;
        }, {}),
      };
    });
  });

  labels.forEach((dateKey) => {
    Object.keys(metricMapping).forEach((category) => {
      Object.entries(metricMapping[category]).forEach(([subCategoryKey, subCategoryObj]) => {
        graphData[category][subCategoryKey].total = leadsStats?.[subCategoryObj?.responseKey];
        productTypes?.forEach((productType) => {
          if (productType?.key == 'all') {
            const value = leadsStats?.items?.[dateKey]?.[subCategoryObj?.responseKey] || 0;
            graphData[category]?.[subCategoryKey]?.[productType?.key]?.push(value);
          } else {
            const value =
              leadsStats?.items?.[dateKey]?.product_wise?.find((product) => product?.ad_product == productType?.key)?.[
                subCategoryObj?.responseKey
              ] || 0;
            graphData[category]?.[subCategoryKey]?.[productType?.key]?.push(value);
          }
        });
      });
    });
  });

  Object.keys(graphData).forEach((key) => {
    if (key == 'calls' && phoneLeadsStats) {
      graphData[key] = { ...graphData?.[key], ...phoneLeadsStats };
    }
  });

  return {
    labels: labels?.map((d) => moment(d)?.locale('en')?.format('MMM DD')),
    data: graphData,
    productTypes,
    tabItems,
  };
};

const widgetDataMapper = (leadsStats) => {
  return [
    {
      key: 'leads',
      title: 'Total Leads',
      value: leadsStats?.sum_lead_count,
      icon: 'TotalLeads',
      iconProps: { size: '24px' },
    },
    {
      key: 'calls',
      title: 'Calls',
      value: leadsStats?.sum_phone_view_count,
      popoverContent: `${t('Calls Clicked')} ${leadsStats?.sum_phone_view_count}`,
      icon: 'IoCall',
      iconColor: '#479eeb',
    },
    {
      key: 'whatsapp',
      title: 'WhatsApp',
      value: leadsStats?.sum_whatsapp_view_count,
      popoverContent: `${t('WhatsApp Clicked')} ${leadsStats?.sum_whatsapp_view_count}`,
      icon: 'RiWhatsappFill',
      iconColor: '#4CAF50',
    },
    {
      key: 'emails',
      title: 'Emails',
      value: leadsStats?.sum_email_lead_count,
      icon: 'MdMail',
    },
  ];
};
export const getLeadsBreakdownData = async (params, user) => {
  const { date_between = getVariousDates(29) } = params || {};
  const startDate = typeof date_between === 'string' ? date_between?.split(',')?.[0] : date_between?.[0];
  const endDate = typeof date_between === 'string' ? date_between?.split(',')?.[1] : date_between?.[1];
  const dateArray = getDateLabels(startDate, endDate, 'YYYY-MM-DD');

  const ParamsObj = {
    start_datetime: startDate,
    end_datetime: endDate,
    'group_by[]': 'date,ad_product',
    'agency_external_ids[]': params?.['agency_external_ids[]'],
    'user_external_ids[]': params?.['user_external_ids[]'],
  };

  const leadsStats = await NetworkService.get(
    `/api/lms/stats/product_stats?${getMultiValuesQueryString(ParamsObj)}&${
      !params?.purpose || params?.purpose === 'all'
        ? `category_ids[]=1&category_ids[]=2`
        : `category_ids[]=${params?.purpose == 'sale' ? 1 : params?.purpose == 'rent' ? 2 : params?.purpose == 'dailyrental' ? 4 : params?.purpose}`
    }`,
  );
  let phoneStats =
    user?.is_lms_enabled && user?.is_call_tracking_enabled && tenantConstants.IS_LMS_ENABLED
      ? await getPhoneStats(params)
      : null;

  if (leadsStats) {
    if (leadsStats.error) {
      return leadsStats;
    } else {
      if (phoneStats?.error) {
        phoneStats = null;
      }
      return {
        widgetData: widgetDataMapper(leadsStats?.data?.stats),
        graphData: graphDataMapper(leadsStats?.data?.stats, user, phoneStats, dateArray),
      };
    }
  }
};
